"use client";
import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";

import FrontPage from "./templates/FrontPage";
import ModulesSummary, { modulesSummaryPageCount } from "./templates/ModulesSummary";
import SurfacesList from "./templates/SurfacesList";
import CostSummary, { COST_SUMMARY_ROWS_PER_PAGE } from "./templates/CostSummary";
import CostDetails from "./templates/CostDetails";
import MetersComparison from "./templates/MetersComparison";
import ResultsPerModule, { resultsPerModulePageCount } from "./templates/ResultsPerModule";
import ResultsPerObject from "./templates/ResultsPerObject";
import ObjectDetail, { objectDetailPageCount } from "./templates/ObjectDetail";
import ResultsPerTenant from "./templates/ResultsPerTenant";
import PonderationMatrice from "./templates/PonderationMatrice";
import EtcDjfNj from "./templates/EtcDjfNj";
import { fmtPeriod, fmtYearLabel, fmtYearFile, fmtLongDateFr, objetDetailLabels } from "./templates/formatters";
import { ROWS_PER_PAGE_LANDSCAPE, ROWS_PER_PAGE_PORTRAIT } from "./templates/pageMetrics";
import { exportRepartitionPdf, appendElementsToPdf } from "./exportRepartitionPdf";
import { exportRepartitionExcel, buildExcelFileName } from "./exportRepartitionExcel";
// Phase 3 (M2): the key/distribution tree diagrams that close the report are the
// same React-Flow diagrams shown on /reports. We render KeyDiagramView frames at
// the end of the preview and capture them with the shared diagram capture helper
// (the répartition orchestrator's naive toPng can't capture React Flow correctly).
import KeyDiagramView from "../Reports/KeyDiagramView";
import { appendDiagramPagesToPdf, prefetchFontEmbedCSS } from "../Reports/diagramPdfCapture";
// Phase 3 (M1.1): PP Neue Montreal — scoped via .repartition-page class. The
// import lives here (the report preview page) so the font payload is not
// downloaded by other routes. See repartition-fonts.css for the rationale.
import "./repartition-fonts.css";

// Phase 3 (C1a): preview + export page for the Répartition report (M1 scope).
//
// Loads every M1 endpoint in parallel, renders all 5 section templates as A4
// pages stacked vertically, and offers a "Download PDF" button that walks the
// rendered DOM and exports the result via exportRepartitionPdf().
//
// Pagination math: the orchestrator stamps "Page X/N" on every page, so we
// compute a base page number per section by counting pages in earlier sections.

const authHeaders = () => ({
  "api-key": process.env.REACT_APP_API_KEY,
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const RepartitionReportPreview = () => {
  const [searchParams] = useSearchParams();
  const buildingId = searchParams.get("building");
  const mainPeriodId = searchParams.get("main_period");
  const compareId = searchParams.get("compare_period");
  // Whether the URL explicitly requested a comparison. The request only sends
  // compare_period_id when the user picked one; the backend auto-resolves the
  // implicit prior period otherwise. The comparison COLUMNS are always shown
  // regardless (see `compareEnabled` below) — they just render "-" when no prior
  // data exists.
  const urlCompareEnabled = !!compareId;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  const [building, setBuilding] = useState(null);
  const [mainPeriod, setMainPeriod] = useState(null);
  const [comparePeriod, setComparePeriod] = useState(null);
  const [modulesData, setModulesData] = useState(null);
  const [surfacesData, setSurfacesData] = useState(null);
  const [costSummaryData, setCostSummaryData] = useState(null);
  const [costDetailsData, setCostDetailsData] = useState(null);
  const [metersData, setMetersData] = useState(null);
  // M2 — Résultats sections
  const [resultsPerModuleData, setResultsPerModuleData] = useState(null);
  const [resultsPerObjectData, setResultsPerObjectData] = useState(null);
  const [objectDetailData, setObjectDetailData] = useState(null);
  const [moduleDetailData, setModuleDetailData] = useState(null);
  const [tenantDetailData, setTenantDetailData] = useState(null);
  const [resultsPerTenantData, setResultsPerTenantData] = useState(null);
  const [ponderationData, setPonderationData] = useState(null);
  const [etcDjfNjData, setEtcDjfNjData] = useState(null);

  // Key diagrams (rendered after the 5 sections, captured into the same PDF).
  const [keys, setKeys] = useState([]);
  const [diagramDataMap, setDiagramDataMap] = useState({});

  const renderRoot = useRef(null);
  // KeyDiagramView reports its rendered A4 frame DOM nodes here, per key id.
  const diagramRefs = useRef({});

  // ─── data load ─────────────────────────────────────────────────────────────

  useEffect(() => {
    (async () => {
      if (!buildingId || !mainPeriodId) {
        setError("Missing building or main period in URL.");
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setError("");
        const baseUrl = process.env.REACT_APP_API_URL;
        const hdr = { headers: authHeaders() };
        const query = urlCompareEnabled
          ? { building_id: buildingId, main_period_id: mainPeriodId, compare_period_id: compareId }
          : { building_id: buildingId, main_period_id: mainPeriodId };

        // `building/details/:id` already includes the period list with
        // white_labelling + logo per period, so we avoid a redundant /periods call.
        const singleParams = { building_id: buildingId, main_period_id: mainPeriodId };
        // Phase 4: the module détaillé is a separate grouping of the SAME endpoint
        // (?group_by=module). Always fetched — the building toggles live on the
        // building payload, which arrives in this very batch, so we can't gate the
        // request on them without a second round-trip. The toggle gates RENDERING
        // (and the page-count math) below instead; one extra query on a page that
        // already fires twelve is a better trade than serialising the fetch.
        const moduleParams = { building_id: buildingId, main_period_id: mainPeriodId, group_by: 'module' };
        const tenantParams = { building_id: buildingId, main_period_id: mainPeriodId, group_by: 'tenant' };
        const [bRes, mod, surf, cs, cd, met, rpm, rpo, odt, mdt, tdt, rpt, pm, edn] = await Promise.all([
          axios.get(`${baseUrl}building/details/${buildingId}`, hdr),
          axios.get(`${baseUrl}repartition-report/modules-summary`, { ...hdr, params: query }),
          axios.get(`${baseUrl}repartition-report/surfaces`, { ...hdr, params: singleParams }),
          axios.get(`${baseUrl}repartition-report/cost-summary`, { ...hdr, params: query }),
          axios.get(`${baseUrl}repartition-report/cost-details`, { ...hdr, params: singleParams }),
          axios.get(`${baseUrl}repartition-report/meters`, { ...hdr, params: query }),
          axios.get(`${baseUrl}repartition-report/results-per-module`, { ...hdr, params: query }),
          axios.get(`${baseUrl}repartition-report/results-per-object`, { ...hdr, params: singleParams }),
          axios.get(`${baseUrl}repartition-report/object-detail`, { ...hdr, params: singleParams }),
          axios.get(`${baseUrl}repartition-report/object-detail`, { ...hdr, params: moduleParams }),
          axios.get(`${baseUrl}repartition-report/object-detail`, { ...hdr, params: tenantParams }),
          axios.get(`${baseUrl}repartition-report/results-per-tenant`, { ...hdr, params: singleParams }),
          axios.get(`${baseUrl}repartition-report/ponderation-matrice`, { ...hdr, params: singleParams }),
          axios.get(`${baseUrl}repartition-report/etc-djf-nj`, { ...hdr, params: singleParams }),
        ]);

        const buildingData = bRes?.data?.data || null;
        setBuilding(buildingData);
        const periods = buildingData?.periods || [];
        setMainPeriod(periods.find((p) => String(p.id) === String(mainPeriodId)) || null);
        // Resolve the comparison period from what the backend actually used: the
        // explicit compare_period from the URL, or the implicit prior period the
        // backend auto-selected (echoed back as compare_period_id in the response).
        // This drives the prior-year column label + the compareEnabled gate below.
        const resolvedCompareId =
          compareId ??
          rpm?.data?.data?.compare_period_id ??
          mod?.data?.data?.compare_period_id ??
          null;
        setComparePeriod(
          resolvedCompareId != null
            ? periods.find((p) => String(p.id) === String(resolvedCompareId)) || null
            : null
        );

        setModulesData(mod?.data?.data || null);
        setSurfacesData(surf?.data?.data || null);
        setCostSummaryData(cs?.data?.data || null);
        setCostDetailsData(cd?.data?.data || null);
        setMetersData(met?.data?.data || null);
        setResultsPerModuleData(rpm?.data?.data || null);
        setResultsPerObjectData(rpo?.data?.data || null);
        setObjectDetailData(odt?.data?.data || null);
        setModuleDetailData(mdt?.data?.data || null);
        setTenantDetailData(tdt?.data?.data || null);
        setResultsPerTenantData(rpt?.data?.data || null);
        setPonderationData(pm?.data?.data || null);
        setEtcDjfNjData(edn?.data?.data || null);

        // Key/distribution diagrams (Report module — note `period_id`, not
        // `main_period_id`). Kept in its own try so a diagram failure degrades to
        // "no diagrams appended" rather than breaking the whole report.
        try {
          const keysRes = await axios.get(`${baseUrl}reports/keys`, {
            ...hdr,
            params: { building_id: buildingId, period_id: mainPeriodId },
          });
          if (keysRes?.data?.status === "success") {
            const fetchedKeys = keysRes.data.data || [];
            setKeys(fetchedKeys);
            if (fetchedKeys.length > 0) {
              const diagramResults = await Promise.all(
                fetchedKeys.map(async (k) => {
                  try {
                    const r = await axios.get(`${baseUrl}reports/keys/${k.id}`, hdr);
                    return r?.data?.status === "success" ? { keyId: k.id, data: r.data.data } : null;
                  } catch (err) {
                    console.error(`Diagram fetch failed for key ${k.id}:`, err);
                    return null;
                  }
                })
              );
              const map = {};
              diagramResults.forEach((res) => { if (res) map[res.keyId] = res.data; });
              setDiagramDataMap(map);
            } else {
              setDiagramDataMap({});
            }
          }
        } catch (err) {
          console.error("Key diagrams load failed (report will export without diagrams):", err);
          setKeys([]);
          setDiagramDataMap({});
        }
      } catch (e) {
        console.error(e);
        setError(e?.response?.data?.message || e?.message || "Failed to load report data.");
      } finally {
        setLoading(false);
      }
    })();
  }, [buildingId, mainPeriodId, compareId, urlCompareEnabled]);

  // ─── derived ───────────────────────────────────────────────────────────────

  // `building_address` is the physical site address ("Place Casa-Bamba 1-3 / 1219
  // Châtelaine" for Etang B) — that's what the example PDF prints in the header.
  // `property_address` is the owner's billing address and would be wrong here.
  const buildingInfo = building
    ? {
      name: building.building_name,
      address: building.building_address || "",
    }
    : null;

  // Compute page count per section so the page-number stamp stays continuous.
  // Must match the ROWS_PER_PAGE constants inside each template.
  const surfacesTotalRows =
    (surfacesData?.surfaces?.length || 0) + (surfacesData?.subtotals?.length || 0) + 1; // +1 grand total
  const SURFACES_ROWS_PER_PAGE = ROWS_PER_PAGE_LANDSCAPE; // donut removed (moved to Modules only); rows now derived from page geometry — single source shared with SurfacesList.jsx
  const surfacesPages = Math.max(1, Math.ceil(surfacesTotalRows / SURFACES_ROWS_PER_PAGE));

  const costDetailsRows = (() => {
    if (!costDetailsData) return 1;
    if (costDetailsData.per_invoice_data_available) {
      let r = 0;
      for (const acct of costDetailsData.details || []) {
        r += (acct.invoices?.length || 0) + 1; // +1 subtotal per account
      }
      return r + 1; // grand total
    }
    return (costDetailsData.details?.length || 0) + 1;
  })();
  const COSTDETAILS_ROWS_PER_PAGE = ROWS_PER_PAGE_LANDSCAPE; // 24 clipped the last row ("information cut between pages"); now derived from page geometry — single source shared with CostDetails.jsx
  const costDetailsPages = Math.max(1, Math.ceil(costDetailsRows / COSTDETAILS_ROWS_PER_PAGE));

  const metersTotalRows = metersData?.meters?.length || 0;
  const METERS_ROWS_PER_PAGE = ROWS_PER_PAGE_PORTRAIT; // derived from page geometry — single source shared with MetersComparison.jsx
  // Pass #3: the Compteurs "Synthèse" charts page was REMOVED — Compteurs is now
  // table pages only. Keep in sync with totalSectionPages in MetersComparison.jsx.
  const metersPages = Math.max(1, Math.ceil(metersTotalRows / METERS_ROWS_PER_PAGE));

  // Cost Summary paginates when the building has more cost accounts than fit one
  // portrait page (Étang A: 41 accounts vs Étang B: 26). Client 2026-07-17
  // reported "Missing 2nd page with missing costs and total" on Étang A — this
  // computes the same page count CostSummary.jsx renders. Total CHF row is only
  // added on the LAST page so it counts as +1 row worth of budget on that page,
  // never on middle pages.
  const costSummaryRowCount = costSummaryData?.costs?.length || 0;
  const COSTSUMMARY_PAGES = Math.max(1, Math.ceil(costSummaryRowCount / COST_SUMMARY_ROWS_PER_PAGE));
  // Plaines-6 feedback (latest): Modules is back to a SINGLE page — table + the
  // "Répartition par catégorie" donut on the same page (client request). Keep in
  // sync with ModulesSummary.jsx (one A4PageFrame).
  // Was hard-coded to 1, which silently truncated buildings with many modules
  // (Espace Tourbillon: 148 modules, ~100 dropped, Total m² row clipped off).
  // Derived from the template's own chunker so the two can never disagree.
  const MODULES_PAGES = modulesSummaryPageCount(modulesData);

  // ─── M2 Pondération matrice page count ───
  // Matrice paginates BOTH rows (22/page) and columns (12/page). Keep in sync
  // with ROWS_PER_PAGE + COLS_PER_PAGE inside PonderationMatrice.jsx.
  const PONDERATION_ROWS_PER_PAGE = 22;
  const PONDERATION_COLS_PER_PAGE = 12;
  const pmRows = ponderationData?.rows?.length || 0;
  const pmCols = ponderationData?.columns?.length || 0;
  // Both factors clamped to ≥1 individually so an empty rows/cols dimension
  // still contributes at least one page — the template does the same via
  // Math.max(N, 1) around its nested loops. Any off-by-one here cascades into
  // wrong "Page X/N" footer numbers on every section that follows.
  const ponderationPages =
    Math.max(1, Math.ceil(pmRows / PONDERATION_ROWS_PER_PAGE)) *
    Math.max(1, Math.ceil(pmCols / PONDERATION_COLS_PER_PAGE));
  // Données climatiques is exactly 1 LANDSCAPE page (pass #3: one combined
  // Début|Fin|ETC|DJF|NJ table, 12 months + Total fit easily).
  const ETCDJFNJ_PAGES = 1;

  // ─── M2 Résultats sections page counts ───
  const RESULTS_ROWS_PER_PAGE = ROWS_PER_PAGE_LANDSCAPE; // +rows to fill empty band; derived from page geometry — single source shared with ResultsList.jsx
  // Client reference PDF page 26: table + bar + donut all fit on one landscape
  // page. Keep in sync with ResultsPerModule.jsx (single A4PageFrame).
  // Same defect as MODULES_PAGES above — derived from the template's chunker.
  const RESULTS_MODULE_PAGES = resultsPerModulePageCount(resultsPerModuleData);
  // Résultats par objet and par locataire are both table-only (charts disabled —
  // matches the reference, see showCharts={false} in ResultsPerObject.jsx /
  // ResultsPerTenant.jsx).
  const rpoRows = (resultsPerObjectData?.objects?.length || 0) + (resultsPerObjectData?.subtotals?.length || 0) + 1;
  const rpoTablePages = Math.max(1, Math.ceil(rpoRows / RESULTS_ROWS_PER_PAGE));
  const resultsPerObjectPages = rpoTablePages; // no charts page for objet
  // Résultat détaillé renders exactly ONE page per object (the per-object graph
  // page was removed — client request). Page-count math + the bookmark cursor in
  // handleExport both assume 1 page/object; keep in sync with ObjectDetail.jsx.
  // Acomptes toggle (per-period, opt-out): when false the report hides the
  // Acomptes/Solde columns and lines. Defaults to true for legacy periods.
  // Declared FIRST because the détaillé page projection below depends on it — the
  // page split changes with Acomptes (2 fewer cost rows fit when Acomptes/Solde
  // land on the last page). Consumed again in the render section further down.
  const acomptesEnabled = mainPeriod?.acomptes_enabled ?? true;

  // Client 2026-08-08: "Graphiques" switch — the Objet/Immeuble chart on the Résultat
  // détaillé pages. BUILDING-scoped (all periods), like detail_*_enabled and unlike
  // the per-period acomptesEnabled above. Defaulted ON via `!== false` so a building
  // row saved before the column existed keeps its chart.
  //
  // Declared up here for the same reason as acomptesEnabled: the détaillé page
  // projection a few lines below depends on it. Switching the graph off returns the
  // chart's ~14 rows to the cost lines, which can turn a 2-page entry into a 1-page
  // entry — so the projection, the bookmark cursors and the render must ALL be given
  // this same value, or the page numbers drift from what is actually drawn.
  const graphEnabled = building?.graph_enabled !== false;

  const allObjectDetails = objectDetailData?.details || [];
  const allModuleDetails = moduleDetailData?.details || [];
  const allTenantDetails = tenantDetailData?.details || [];

  // ── Large-building guard ───────────────────────────────────────────────────
  // The PDF export is browser-side: every A4 page is built in the DOM and then
  // rasterised to a PNG, so PAGES are what exhaust memory / time out — not the
  // number of entries. Past the budget we KEEP every summary section and OMIT the
  // détaillé pages, replacing them with a single note page.
  //
  // MEASURED IN PAGES (fixed 31.07.2026). It used to count ENTRIES with a cap of
  // 200, which was a fair proxy only while one entry meant one page. Restoring the
  // inline chart made entries over ~30 cost accounts span TWO pages, so the proxy
  // broke: Étang A passes an entry cap at 157 entries while actually producing 314
  // pages — right past the level the guard was meant to enforce.
  //
  // The budget is deliberately 400, not 200. A straight unit swap would have
  // capped Étang A's objet détaillé and DELETED content the client currently
  // receives, which is worse than a slow export. 400 keeps Étang A's 314 pages
  // while still catching the genuinely extreme case (Espace Tourbillon: 945
  // entries ≈ 1'890 pages). Tune it here if a real export proves the ceiling is
  // lower — this is the one number to move, and the projection below will follow.
  const DETAIL_PAGE_BUDGET = 400;
  // Applied PER SECTION, not once for the whole détaillé block: Espace Tourbillon
  // has 945 objects (capped) but only 148 modules, which render fine; one shared
  // flag would have suppressed the module pages along with the object ones.
  // The variant matters: a module page holds more cost rows than an objet/locataire
  // page because its header is 2 rows shorter, so each section must be projected with
  // its OWN variant or the page numbers drift from the render.
  const projectPages = (list, variant) =>
    list.reduce((n, d) => n + objectDetailPageCount(d, acomptesEnabled, variant, graphEnabled), 0);
  const objectProjectedPages = projectPages(allObjectDetails, 'object');
  const moduleProjectedPages = projectPages(allModuleDetails, 'module');
  const tenantProjectedPages = projectPages(allTenantDetails, 'tenant');
  const detailCapped = objectProjectedPages > DETAIL_PAGE_BUDGET;
  const moduleDetailCapped = moduleProjectedPages > DETAIL_PAGE_BUDGET;
  const tenantDetailCapped = tenantProjectedPages > DETAIL_PAGE_BUDGET;

  // The details the report actually renders: all of them under the budget, none
  // above it (the note page stands in). ObjectDetail + the outline loop + the
  // page-count math all consume THESE lists so they never drift.
  const objectDetails = detailCapped ? [] : allObjectDetails;
  const moduleDetails = moduleDetailCapped ? [] : allModuleDetails;
  const tenantDetails = tenantDetailCapped ? [] : allTenantDetails;

  // ── Phase 4: section gating ───────────────────────────────────────────────
  // Building-level toggles (all periods), unlike acomptesEnabled which is
  // per-period. Defaults preserve today's report exactly: objet ON, module OFF.
  // A toggled-off section contributes ZERO pages, so every base.* below and
  // grandTotalPages stay identical to Phase 3 for existing buildings.
  const moduleDetailEnabled = building?.detail_module_enabled === true;
  const objectDetailEnabled = building?.detail_object_enabled !== false;
  const tenantDetailEnabled = building?.detail_tenant_enabled === true;
  // A capped section still occupies exactly 1 page — the note page.
  const objectDetailPages = detailCapped ? 1 : Math.max(1, objectProjectedPages);
  const moduleDetailPagesUncapped = moduleDetailCapped ? 1 : Math.max(1, moduleProjectedPages);
  const moduleDetailPages = moduleDetailEnabled ? moduleDetailPagesUncapped : 0;
  const tenantDetailPagesUncapped = tenantDetailCapped ? 1 : Math.max(1, tenantProjectedPages);
  const tenantDetailPages = tenantDetailEnabled ? tenantDetailPagesUncapped : 0;
  // The objet section can now be switched off too (it never could before).
  const objectDetailPagesGated = objectDetailEnabled ? objectDetailPages : 0;
  const rptRows = (resultsPerTenantData?.objects?.length || 0) + (resultsPerTenantData?.subtotals?.length || 0) + 1;
  const rptTablePages = Math.max(1, Math.ceil(rptRows / RESULTS_ROWS_PER_PAGE));
  const resultsPerTenantPages = rptTablePages; // table only — no charts page (matches reference)

  // The cover (FrontPage) is page 1; every section shifts down by FRONT_PAGES.
  const FRONT_PAGES = 1;

  // Section page-number bases (1-indexed, continuous across sections).
  const base = {};
  base.modules = 1 + FRONT_PAGES;
  base.surfaces = base.modules + MODULES_PAGES;
  base.costSummary = base.surfaces + surfacesPages;
  base.costDetails = base.costSummary + COSTSUMMARY_PAGES;
  base.meters = base.costDetails + costDetailsPages;
  base.ponderation = base.meters + metersPages;
  base.etcDjfNj = base.ponderation + ponderationPages;
  // Phase 4 section order (client, 28.07.2026): each détaillé follows its OWN
  // summary — par module → détaillé (module) → par objet → détaillé (objet) →
  // par locataire → [détaillé (locataire), M2]. A toggled-off section contributes
  // 0 pages, so with the default flags these numbers equal Phase 3's exactly.
  base.resultsPerModule = base.etcDjfNj + ETCDJFNJ_PAGES;
  base.moduleDetail = base.resultsPerModule + RESULTS_MODULE_PAGES;
  base.resultsPerObject = base.moduleDetail + moduleDetailPages;
  base.objectDetail = base.resultsPerObject + resultsPerObjectPages;
  base.resultsPerTenant = base.objectDetail + objectDetailPagesGated;
  base.tenantDetail = base.resultsPerTenant + resultsPerTenantPages;
  const basePages = base;
  const grandTotalPages =
    FRONT_PAGES + MODULES_PAGES + surfacesPages + COSTSUMMARY_PAGES + costDetailsPages + metersPages +
    ponderationPages + ETCDJFNJ_PAGES +
    RESULTS_MODULE_PAGES + moduleDetailPages + resultsPerObjectPages + objectDetailPagesGated +
    resultsPerTenantPages + tenantDetailPages;

  // Range-aware year labels: a single-calendar-year period shows "2024"; a
  // cross-year period (e.g. 01.07.2021–30.06.2022) shows the short range "21/22".
  // This single object feeds every template's column headers, chart titles and
  // legends, so the label propagates everywhere from here.
  // Prior-year column label. Use the resolved comparison period when there is
  // one; otherwise fall back to "main start year − 1" so the comparison columns
  // (which are always shown — see below) still carry a sensible "2023"-style
  // header even when no prior period exists in the data.
  const priorYearFallback = (() => {
    const sy = mainPeriod?.start_date ? new Date(mainPeriod.start_date).getFullYear() : null;
    return sy && !Number.isNaN(sy) ? String(sy - 1) : "";
  })();
  const periodYears = mainPeriod
    ? {
      current: fmtYearLabel(mainPeriod.start_date, mainPeriod.end_date),
      prior: comparePeriod ? fmtYearLabel(comparePeriod.start_date, comparePeriod.end_date) : priorYearFallback,
    }
    : { current: "Current", prior: "Prior" };

  // The comparison columns (prior-year HT, Diff. HT, Diff. %) are ALWAYS shown to
  // match the reference layout. When a comparison period was resolved (explicit
  // URL param or the implicit prior period auto-selected by the backend) the
  // cells carry real values; otherwise they render "-" via fmtNum/fmtPct.
  const compareEnabled = true;

  const periodLabel = mainPeriod ? fmtPeriod(mainPeriod.start_date, mainPeriod.end_date) : "";

  const whiteLabelling = !!mainPeriod?.white_labelling;
  const logoUrl = mainPeriod?.logo || null;

  // ─── key diagrams ──────────────────────────────────────────────────────────

  // KeyDiagramView calls this ~1s after each diagram renders, handing us its A4
  // frame DOM nodes so we can capture them during export.
  const handleDiagramReady = (keyId, elements) => {
    if (elements?.length) diagramRefs.current[keyId] = elements;
  };

  // Safety gate: wait until every expected key has reported its DOM frames (or a
  // timeout). Diagrams render on mount, so they're usually ready well before the
  // user clicks Download — this just guards the rare not-yet-ready case.
  const waitForDiagrams = (expectedKeyIds, timeoutMs = 8000) =>
    new Promise((resolve) => {
      if (!expectedKeyIds.length) return resolve();
      const start = Date.now();
      const tick = () => {
        const ready = expectedKeyIds.every((id) => (diagramRefs.current[id]?.length || 0) > 0);
        if (ready || Date.now() - start > timeoutMs) return resolve();
        setTimeout(tick, 200);
      };
      tick();
    });

  // ─── PDF export ────────────────────────────────────────────────────────────

  const handleExport = async () => {
    if (!renderRoot.current) return;
    setExporting(true);
    try {
      // Split section pages by an explicit per-page marker: M2 Résultats pages
      // carry data-rr-results-page and are captured LATER (phase 2.5, after the
      // diagrams) to match the reference order. Phase-1 = everything else (cover +
      // M1). Using the page's own attribute (not a wrapper + closest) is robust to
      // DOM nesting.
      const allRrPages = Array.from(renderRoot.current.querySelectorAll("[data-rr-page]"));
      const resultsPages = allRrPages.filter((el) => el.hasAttribute("data-rr-results-page"));
      const rrPages = allRrPages.filter((el) => !el.hasAttribute("data-rr-results-page"));
      // Guard: the two groups must be exact complements — every section page is
      // captured exactly once, none dropped or duplicated.
      if (rrPages.length + resultsPages.length !== allRrPages.length) {
        console.error("RR export page-split mismatch", { all: allRrPages.length, phase1: rrPages.length, results: resultsPages.length });
      }
      const expectedKeyIds = keys.map((k) => k.id);
      // Diagnostic: confirms the current bundle ran and how each phase split.
      console.info("[RR export] phases", { m1: rrPages.length, diagrams: expectedKeyIds.length, results: resultsPages.length });
      setProgress({ done: 0, total: rrPages.length + expectedKeyIds.length + resultsPages.length });

      // Phase 1 — cover + M1 section pages. Returns the pdf instead of saving so
      // we can append the diagram / Résultats / schema pages to the SAME document.
      const result = await exportRepartitionPdf({
        pageElements: rrPages,
        save: false,
        onProgress: (done, total) => setProgress({ done, total: total + expectedKeyIds.length + resultsPages.length }),
      });
      const pdf = result?.pdf;
      const sectionPageCount = result?.pageCount || rrPages.length;

      // Bookmark page-number tracking — recorded from ACTUAL appended positions so
      // the outline can never drift (the previous `shift`/chart-page reconstruction
      // did). diagKeyPages preserves each key's page count IN keys-order; the
      // per-key bookmark loop advances a cursor by exactly these counts.
      const diagKeyPages = []; // [{ id, key_name, pages }] for keys that produced pages
      let resultsStartPage = null; // 1-indexed page where phase-2.5 begins

      // Phase 2 — key diagrams (kept with their own native footer; not part of the
      // section "Page X/N" numbering, matching the reference deliverable).
      if (pdf && expectedKeyIds.length) {
        await waitForDiagrams(expectedKeyIds);
        // Each ready entry is { el, nodes } (KeyDiagramView reports nodes so the PDF
        // capture can size from node coords). Tolerate a bare el for safety.
        const diagramPages = keys.flatMap((k) =>
          (diagramRefs.current[k.id] || []).map((entry, pi) => ({
            key: k,
            el: entry?.el || entry,
            nodes: entry?.nodes || [],
            pi,
          }))
        );
        // Per-key page counts (keys-order) for the diagram bookmarks — taken from
        // the SAME array we append, so it matches the real pages exactly.
        for (const k of keys) {
          const n = (diagramRefs.current[k.id] || []).length;
          if (n > 0) diagKeyPages.push({ id: k.id, key_name: k.key_name, pages: n });
        }
        if (diagramPages.length) {
          const fontEmbedCSS = await prefetchFontEmbedCSS(diagramPages[0].el);
          await appendDiagramPagesToPdf({
            pdf,
            pages: diagramPages,
            fontEmbedCSS,
            startPdfPageCount: sectionPageCount,
            onProgress: (done) =>
              setProgress({ done: sectionPageCount + done, total: sectionPageCount + diagramPages.length }),
          });
        }
      }

      // Phase 2.5 — append the M2 Résultats sections AFTER the diagrams (and
      // before the schema), matching the reference order. Captured via the same
      // toPng pipeline as the section pages (plain DOM, no React Flow).
      if (pdf && resultsPages.length) {
        let fontEmbedCSS = "";
        try {
          fontEmbedCSS = await prefetchFontEmbedCSS(resultsPages[0]);
        } catch {
          fontEmbedCSS = "";
        }
        const before = pdf.getNumberOfPages();
        resultsStartPage = before + 1; // first phase-2.5 page (actual position)
        const added = await appendElementsToPdf({
          pdf,
          elements: resultsPages,
          fontEmbedCSS,
          startPdfPageCount: before,
          onProgress: (done) => setProgress({ done, total: done }),
        });
        if (added !== resultsPages.length) {
          console.error("RR export: Résultats pages added != expected", { added, expected: resultsPages.length });
        }
      }

      // ── PDF bookmarks (client-approved: hierarchical outline) ──
      // Top-level per section, sub-bookmarks per object under "Résultat détaillé".
      // Phase-1 sections use their basePage numbers unchanged; phase-2.5 sections
      // are shifted by the diagram count inserted between them.
      try {
        if (pdf && pdf.outline && typeof pdf.outline.add === "function") {
          const phase1End = sectionPageCount;
          // Phase-1 sections keep their basePages numbers (they were captured
          // first, in order). The phase-2.5 Résultats sections are anchored at the
          // ACTUAL first résultats page recorded during the append (resultsStartPage)
          // — never reconstructed — so diagram-count changes or a skipped diagram
          // page can't drift them. Fallback to the section-order start if phase 2.5
          // didn't run.
          const resultsStart = resultsStartPage != null
            ? resultsStartPage
            : basePages.resultsPerModule;
          // Relative offset of each phase-2.5 section within the Résultats block.
          const relTo = (page) => resultsStart + (page - basePages.resultsPerModule);

          pdf.outline.add(null, "Page de garde", { pageNumber: 1 });
          pdf.outline.add(null, "Modules", { pageNumber: basePages.modules });
          pdf.outline.add(null, "Surfaces", { pageNumber: basePages.surfaces });
          pdf.outline.add(null, "Résumé des coûts", { pageNumber: basePages.costSummary });
          pdf.outline.add(null, "Coûts", { pageNumber: basePages.costDetails });
          pdf.outline.add(null, "Compteurs", { pageNumber: basePages.meters });
          pdf.outline.add(null, "Matrice pondérations", { pageNumber: basePages.ponderation });
          pdf.outline.add(null, "Données climatiques", { pageNumber: basePages.etcDjfNj });
          // Diagrammes des clés — one bookmark PER KEY, nested under a parent.
          // Diagram pages start right after phase 1; each key's page count comes
          // from diagKeyPages (built from the SAME array that was appended), so the
          // cursor tracks the real pages even if a key produced 0 pages.
          if (diagKeyPages.length) {
            const diagParent = pdf.outline.add(null, "Diagrammes des clés", { pageNumber: phase1End + 1 });
            let diagCursor = phase1End + 1;
            for (const dk of diagKeyPages) {
              pdf.outline.add(diagParent, dk.key_name || `Clé ${dk.id}`, { pageNumber: diagCursor });
              diagCursor += dk.pages;
            }
          }
          // Phase 4 (client 28.07.2026): each détaillé nests INSIDE its own summary
          // bookmark rather than sitting at top level, and each group gets a child.
          const perModuleParent = pdf.outline.add(null, "Résultats par module", { pageNumber: relTo(basePages.resultsPerModule) });
          if (moduleDetailEnabled) {
            const modDetailParent = pdf.outline.add(perModuleParent, "Résultat détaillé (module)", { pageNumber: relTo(basePages.moduleDetail) });
            let modCursor = relTo(basePages.moduleDetail);
            for (const d of moduleDetails) {
              pdf.outline.add(modDetailParent, d.module || "Module", { pageNumber: modCursor });
              // Variant matters — a module page holds more rows than an objet page.
              modCursor += objectDetailPageCount(d, acomptesEnabled, 'module', graphEnabled);
            }
          }
          const perObjectParent = pdf.outline.add(null, "Résultats par objet", { pageNumber: relTo(basePages.resultsPerObject) });
          const detailParent = objectDetailEnabled
            ? pdf.outline.add(perObjectParent, "Résultat détaillé (objet)", { pageNumber: relTo(basePages.objectDetail) })
            : null;
          // Per-object bookmarks — each object may span several landscape pages
          // (objectDetailPageCount), so advance the cursor by that count; the
          // bookmark points to the object's FIRST page. Iterate the SAME (possibly
          // capped) list the section renders. When capped there are no per-object
          // pages — the note page is the parent.
          // Guarded on detailParent: when the objet toggle is OFF there is no parent
          // and no pages, and adding these with a null parent would scatter one
          // top-level bookmark per object across the outline.
          if (detailParent) {
            let cursor = relTo(basePages.objectDetail);
            // A code imported on two modules has a page per part, so a repeated code
            // carries its module ("06862.20.2100.07 (90710)") — see objetDetailLabels.
            const objetLabels = objetDetailLabels(objectDetails);
            objectDetails.forEach((d, i) => {
              pdf.outline.add(detailParent, objetLabels[i], { pageNumber: cursor });
              cursor += objectDetailPageCount(d, acomptesEnabled, 'object', graphEnabled);
            });
          }
          const perTenantParent = pdf.outline.add(null, "Résultats par locataire", { pageNumber: relTo(basePages.resultsPerTenant) });
          if (tenantDetailEnabled) {
            const tenDetailParent = pdf.outline.add(perTenantParent, "Résultat détaillé (locataire)", { pageNumber: relTo(basePages.tenantDetail) });
            let tenCursor = relTo(basePages.tenantDetail);
            for (const d of tenantDetails) {
              pdf.outline.add(tenDetailParent, d.locataire || "Locataire", { pageNumber: tenCursor });
              tenCursor += objectDetailPageCount(d, acomptesEnabled, 'tenant', graphEnabled);
            }
          }
        }
      } catch (bmErr) {
        console.warn("PDF outline (bookmarks) failed — continuing without them:", bmErr);
      }

      // Client request: the "Schéma de principe des flux énergétiques" page was
      // removed from the report (it was not correct), so nothing is appended here.

      // Sanitize the building name so filesystem-illegal characters (e.g. the "/"
      // in "Primerose 35 / Pl. 2"), whitespace and stray dots collapse to single
      // underscores — otherwise the browser mangles "/" into "_" leaving "___".
      // Year token comes from fmtYearFile ("21-22" cross-year, "2024" single-year),
      // whose own doc cites the target form "Repartition_Plaines_6_21-22.pdf".
      const safeName = (building?.building_name || "report")
        .replace(/[\\/:*?"<>|.\s]+/g, "_")
        .replace(/_+/g, "_")
        .replace(/^_+|_+$/g, "");
      const fileName = `Repartition_${safeName || "report"}_${fmtYearFile(mainPeriod?.start_date, mainPeriod?.end_date) || "report"}.pdf`;
      if (pdf) pdf.save(fileName);
    } catch (e) {
      console.error(e);
      alert(e?.message || "PDF export failed.");
    } finally {
      setExporting(false);
    }
  };

  // ─── Excel export ──────────────────────────────────────────────────────────
  // Native-data .xlsx of the Results tables (Module / Objet / Locataire + one
  // Résultat détaillé sheet per object), built entirely from data already in
  // state — no re-fetch. Respects the acomptes toggle like the PDF/screen.
  const handleExportExcel = () => {
    setExportingExcel(true);
    try {
      const fileName = buildExcelFileName(
        building?.building_name,
        mainPeriod?.start_date,
        mainPeriod?.end_date,
      );
      exportRepartitionExcel({
        modulesData,
        surfacesData,
        costSummaryData,
        costDetailsData,
        metersData,
        ponderationData,
        etcDjfNjData,
        resultsPerModuleData,
        resultsPerObjectData,
        resultsPerTenantData,
        objectDetailData,
        // Phase 4: the Excel mirrors the PDF exactly — same toggles, same order,
        // nothing extra (client, 28.07.2026). Pass the gates through rather than
        // re-deriving them here, so the workbook can never disagree with the pages.
        moduleDetailData,
        tenantDetailData,
        moduleDetailEnabled,
        objectDetailEnabled,
        tenantDetailEnabled,
        periodYears,
        periodLabel,
        acomptesEnabled,
        fileName,
      });
    } catch (e) {
      console.error(e);
      alert(e?.message || "Excel export failed.");
    } finally {
      setExportingExcel(false);
    }
  };

  // ─── render ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="admin-page">
        <div className="key-card mt-4 p-4">Loading report…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-page">
        <div className="key-card mt-4 p-4">
          <div className="alert alert-warning py-2 small">{error}</div>
          <Link className="btn btn-outline-secondary" to={`/repartition-report?${searchParams.toString()}`}>
            Back to selector
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-page-title d-flex align-items-center justify-content-between">
        <h1 className="page-section-title mb-0">Répartition — preview</h1>
        <div className="d-flex gap-2 align-items-center">
          {exporting && (
            <span className="text-muted small">
              Capturing {progress.done}/{progress.total}…
            </span>
          )}
          <button
            className="btn btn-outline-primary"
            onClick={handleExportExcel}
            disabled={exportingExcel || !resultsPerObjectData}
          >
            {exportingExcel ? "Exporting…" : "Download Excel"}
          </button>
          <button className="btn btn-outline-primary" onClick={handleExport} disabled={exporting}>
            {exporting ? "Exporting…" : "Download PDF"}
          </button>
          <Link className="btn btn-outline-secondary" to={`/repartition-report?${searchParams.toString()}`}>
            Back
          </Link>
        </div>
      </div>

      {/* Render the templates. They are visible so html-to-image can capture them;
          the orchestrator walks every [data-rr-page] inside renderRoot. */}
      <div ref={renderRoot} style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 16 }}>
        <FrontPage
          building={building}
          periodLabel={periodLabel}
          periodYears={periodYears}
          author={JSON.parse(localStorage.getItem("user") || "{}")?.name || ""}
          reportDate={fmtLongDateFr()}
          totalTtc={costSummaryData?.totals?.main_ttc}
          totalSurface={surfacesData?.grand_total}
          whiteLabelling={whiteLabelling}
          logoUrl={logoUrl}
          pageNumber={1}
          grandTotalPages={grandTotalPages}
        />
        <ModulesSummary
          data={modulesData}
          building={buildingInfo}
          periodLabel={periodLabel}
          periodYears={periodYears}
          compareEnabled={compareEnabled}
          whiteLabelling={whiteLabelling}
          logoUrl={logoUrl}
          basePageNumber={basePages.modules}
          grandTotalPages={grandTotalPages}
        />
        <SurfacesList
          data={surfacesData}
          building={buildingInfo}
          periodLabel={periodLabel}
          whiteLabelling={whiteLabelling}
          logoUrl={logoUrl}
          basePageNumber={basePages.surfaces}
          grandTotalPages={grandTotalPages}
        />
        <CostSummary
          data={costSummaryData}
          building={buildingInfo}
          periodLabel={periodLabel}
          periodYears={periodYears}
          compareEnabled={compareEnabled}
          whiteLabelling={whiteLabelling}
          logoUrl={logoUrl}
          basePageNumber={basePages.costSummary}
          grandTotalPages={grandTotalPages}
        />
        <CostDetails
          data={costDetailsData}
          building={buildingInfo}
          periodLabel={periodLabel}
          whiteLabelling={whiteLabelling}
          logoUrl={logoUrl}
          basePageNumber={basePages.costDetails}
          grandTotalPages={grandTotalPages}
        />
        <MetersComparison
          data={metersData}
          building={buildingInfo}
          periodLabel={periodLabel}
          periodYears={periodYears}
          compareEnabled={compareEnabled}
          whiteLabelling={whiteLabelling}
          logoUrl={logoUrl}
          basePageNumber={basePages.meters}
          grandTotalPages={grandTotalPages}
        />
        <PonderationMatrice
          data={ponderationData}
          building={buildingInfo}
          periodLabel={periodLabel}
          whiteLabelling={whiteLabelling}
          logoUrl={logoUrl}
          basePageNumber={basePages.ponderation}
          grandTotalPages={grandTotalPages}
        />
        <EtcDjfNj
          data={etcDjfNjData}
          building={buildingInfo}
          periodLabel={periodLabel}
          whiteLabelling={whiteLabelling}
          logoUrl={logoUrl}
          basePageNumber={basePages.etcDjfNj}
          grandTotalPages={grandTotalPages}
        />

        {/* Key/distribution diagrams (graphs) — rendered BEFORE the Résultats
            sections to match the reference order: M1 → graphs → Résultats → schema.
            They use the `.diagram-a4-frame` class (NOT data-rr-page), so the
            section capture ignores them; they're captured in phase 2 of
            handleExport via the shared React-Flow capture helper. */}
        {keys.map((k) =>
          diagramDataMap[k.id] ? (
            <div key={`diagram-${k.id}`} data-diagram-wrapper>
              <KeyDiagramView
                keyId={k.id}
                reportData={diagramDataMap[k.id]}
                onDiagramReady={handleDiagramReady}
                whiteLabellingEnabled={whiteLabelling}
                customLogoUrl={logoUrl}
              />
            </div>
          ) : null
        )}

        {/* M2 — Résultats sections, rendered AFTER the graphs (matching the
            reference). Each page carries data-rr-results-page so the PDF exporter
            captures them in phase 2.5 (after the diagrams); DOM order here also
            drives the on-screen preview order. */}
        <div data-rr-results-group>
          <ResultsPerModule
            data={resultsPerModuleData}
            building={buildingInfo}
            periodLabel={periodLabel}
            periodYears={periodYears}
            compareEnabled={compareEnabled}
            whiteLabelling={whiteLabelling}
            logoUrl={logoUrl}
            basePageNumber={basePages.resultsPerModule}
            grandTotalPages={grandTotalPages}
          />
          {/* Phase 4: Résultat détaillé (module) — directly after its own summary,
              per the client's requested order. Rendered only when the building
              toggle is on; when off it contributes 0 pages (see the math above). */}
          {moduleDetailEnabled && (
            <ObjectDetail
              variant="module"
              data={moduleDetailCapped ? { ...moduleDetailData, details: [] } : moduleDetailData}
              capped={moduleDetailCapped}
              omittedCount={allModuleDetails.length}
              omittedPages={moduleProjectedPages}
              capThreshold={DETAIL_PAGE_BUDGET}
              building={buildingInfo}
              periodLabel={periodLabel}
              periodYears={periodYears}
              whiteLabelling={whiteLabelling}
              logoUrl={logoUrl}
              acomptesEnabled={acomptesEnabled}
              graphEnabled={graphEnabled}
              basePageNumber={basePages.moduleDetail}
              grandTotalPages={grandTotalPages}
            />
          )}
          <ResultsPerObject
            data={resultsPerObjectData}
            building={buildingInfo}
            periodLabel={periodLabel}
            periodYears={periodYears}
            whiteLabelling={whiteLabelling}
            logoUrl={logoUrl}
            acomptesEnabled={acomptesEnabled}
            basePageNumber={basePages.resultsPerObject}
            grandTotalPages={grandTotalPages}
          />
          {objectDetailEnabled && (
            <ObjectDetail
              variant="object"
              data={detailCapped ? { ...objectDetailData, details: [] } : objectDetailData}
              capped={detailCapped}
              omittedCount={allObjectDetails.length}
              omittedPages={objectProjectedPages}
              capThreshold={DETAIL_PAGE_BUDGET}
              building={buildingInfo}
              periodLabel={periodLabel}
              periodYears={periodYears}
              whiteLabelling={whiteLabelling}
              logoUrl={logoUrl}
              acomptesEnabled={acomptesEnabled}
              graphEnabled={graphEnabled}
              basePageNumber={basePages.objectDetail}
              grandTotalPages={grandTotalPages}
            />
          )}
          <ResultsPerTenant
            data={resultsPerTenantData}
            building={buildingInfo}
            periodLabel={periodLabel}
            periodYears={periodYears}
            whiteLabelling={whiteLabelling}
            logoUrl={logoUrl}
            acomptesEnabled={acomptesEnabled}
            basePageNumber={basePages.resultsPerTenant}
            grandTotalPages={grandTotalPages}
          />
          {/* Phase 4 M2: Résultat détaillé (locataire) — closes the results block,
              directly after its own summary, per the client's requested order. */}
          {tenantDetailEnabled && (
            <ObjectDetail
              variant="tenant"
              data={tenantDetailCapped ? { ...tenantDetailData, details: [] } : tenantDetailData}
              capped={tenantDetailCapped}
              omittedCount={allTenantDetails.length}
              omittedPages={tenantProjectedPages}
              capThreshold={DETAIL_PAGE_BUDGET}
              building={buildingInfo}
              periodLabel={periodLabel}
              periodYears={periodYears}
              whiteLabelling={whiteLabelling}
              logoUrl={logoUrl}
              acomptesEnabled={acomptesEnabled}
              graphEnabled={graphEnabled}
              basePageNumber={basePages.tenantDetail}
              grandTotalPages={grandTotalPages}
            />
          )}
        </div>

        {/* The "Schéma de principe des flux énergétiques" page was removed at the
            client's request (it was not correct). */}
      </div>
    </div>
  );
};

export default RepartitionReportPreview;

